import type { INotificationService, NotificationListItem } from '../../services/notifications/INotificationService';
import { emitNotificationInboxHint, subscribeNotificationInboxHint } from './notificationInboxHint';

export type NotificationInboxSnapshot = {
  hasUnread: boolean;
  unreadCount: number;
  items: NotificationListItem[];
  nextCursor: string | null;
  loading: boolean;
};

const EMPTY_SNAPSHOT: NotificationInboxSnapshot = {
  hasUnread: false,
  unreadCount: 0,
  items: [],
  nextCursor: null,
  loading: false,
};

const DEBOUNCE_MS = 1000;

/**
 * Host session state for unread status/count and the open inbox page.
 *
 * Coalesces inbox hints with a trailing 1 s debounce (matching
 * `PresenceManager`) and keeps a single unread read in flight: two hints inside
 * the window produce one service read, and a hint that arrives during a read
 * schedules exactly one follow-up.
 */
export class NotificationCoordinator {
  private snapshot: NotificationInboxSnapshot = EMPTY_SNAPSHOT;
  private listeners: Array<() => void> = [];
  private unsubscribeHint: (() => void) | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  private followUpPending = false;
  private started = false;

  constructor(
    private readonly deps: {
      service: INotificationService;
      isNotificationsScreenMounted: () => boolean;
      /** Optimistic profile badge update after a successful mark-read. */
      onUnreadDecremented?: () => void;
      /** Keep profile badges aligned whenever unread is re-read. */
      onUnreadCountChanged?: (count: number) => void;
    },
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.unsubscribeHint = subscribeNotificationInboxHint(() => this.onInboxHint());
    void this.runCoalescedReadWithRetry();
  }

  stop(): void {
    this.started = false;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.unsubscribeHint?.();
    this.unsubscribeHint = null;
  }

  getSnapshot(): NotificationInboxSnapshot {
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  onInboxHint(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.runCoalescedRead();
    }, DEBOUNCE_MS);
  }

  async markRead(notificationId: string): Promise<void> {
    try {
      const wasUnread = this.snapshot.items.find((item) => item.id === notificationId)?.read === false;
      await this.deps.service.markRead(notificationId);
      const items = this.snapshot.items.map((item) =>
        item.id === notificationId ? { ...item, read: true } : item,
      );
      // Optimistic local decrement; the server's per-user mark-read hint
      // re-reads unreadCount shortly after so a multi-page inbox self-corrects.
      const unreadCount = wasUnread
        ? Math.max(0, this.snapshot.unreadCount - 1)
        : this.snapshot.unreadCount;
      this.setSnapshot({
        ...this.snapshot,
        items,
        unreadCount,
        hasUnread: unreadCount > 0,
      });
      if (wasUnread) this.deps.onUnreadDecremented?.();
    } catch {
      // A failed mark-read leaves the row unread; do not write session storage.
    }
  }

  async loadMore(): Promise<void> {
    const cursor = this.snapshot.nextCursor;
    if (!cursor) return;
    const page = await this.deps.service.getPage(cursor);
    this.setSnapshot({
      ...this.snapshot,
      items: [...this.snapshot.items, ...page.items],
      nextCursor: page.nextCursor,
    });
  }

  /**
   * Force a read when the notifications screen opens. Startup only loads
   * `{ hasUnread, unreadCount }` while the hub is up; without this, the inbox
   * list stays empty until a websocket hint arrives while the page is mounted.
   */
  async loadInbox(): Promise<void> {
    this.setSnapshot({ ...this.snapshot, loading: true });
    await this.refresh({ forcePage: true });
  }

  /** Immediate unread re-read (no debounce) for cold-open / navigate safety nets. */
  refreshUnread(): void {
    void this.runCoalescedRead();
  }

  private async refresh(options: { forcePage?: boolean } = {}): Promise<void> {
    try {
      const unread = await this.deps.service.getUnread();
      const onInboxScreen = options.forcePage || this.deps.isNotificationsScreenMounted();
      // Badges must update without opening the inbox. If /unread is still 0
      // (active-set race right after publish), pull one page to reconcile.
      const shouldLoadPage = onInboxScreen || unread.unreadCount === 0;

      let unreadCount = unread.unreadCount;
      let items = this.snapshot.items;
      let nextCursor = this.snapshot.nextCursor;

      if (shouldLoadPage) {
        const page = await this.deps.service.getPage();
        const pageUnread = page.items.reduce((n, item) => n + (item.read ? 0 : 1), 0);
        unreadCount = Math.max(unreadCount, pageUnread);
        if (onInboxScreen) {
          items = page.items;
          nextCursor = page.nextCursor;
        }
      }

      this.setSnapshot({
        ...this.snapshot,
        hasUnread: unreadCount > 0,
        unreadCount,
        items,
        nextCursor,
        loading: false,
      });
      this.deps.onUnreadCountChanged?.(unreadCount);
    } catch (err) {
      console.error('[NotificationCoordinator] inbox read failed', err);
      this.setSnapshot({ ...this.snapshot, loading: false });
      // A failed read leaves the last snapshot in place; the next hint retries.
      // This must not reject into React or produce an unhandled rejection.
    }
  }

  private async runCoalescedRead(): Promise<void> {
    if (this.inFlight) {
      this.followUpPending = true;
      return;
    }
    this.inFlight = true;
    try {
      await this.refresh();
    } finally {
      this.inFlight = false;
      if (this.followUpPending) {
        this.followUpPending = false;
        await this.runCoalescedRead();
      }
    }
  }

  /** Startup read: a few spaced attempts so a brief 503/auth race does not stick on empty. */
  private async runCoalescedReadWithRetry(): Promise<void> {
    for (const delayMs of [0, 500, 1500]) {
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      await this.runCoalescedRead();
    }
  }

  private setSnapshot(next: NotificationInboxSnapshot): void {
    this.snapshot = next;
    this.listeners.forEach((listener) => listener());
  }
}
